import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { leveledContent } from '../../config/consequencesRecommendations';
import './Consequences.css';

// Importamos íconos específicos para cada tipo de consecuencia
const consequenceIcons = {
  physical: '🫀', // Físico
  emotional: '😔', // Emocional
  cognitive: '🧠', // Cognitivo
  behavioral: '🚶', // Conductual
  social: '👥', // Social
  mindfulness: '🧘', // Relacionado con mindfulness
};

const Consequences = ({ areaId, level, color }) => {
  const [symptomSpecificContent, setSymptomSpecificContent] = useState([]);
  
  useEffect(() => {
    // Buscamos contenido específico por área y nivel en el archivo de configuración
    if (leveledContent[areaId.toLowerCase()] && 
        leveledContent[areaId.toLowerCase()][level.toUpperCase()] && 
        leveledContent[areaId.toLowerCase()][level.toUpperCase()].consequences) {
      
      // Usamos las consecuencias específicas para este tipo de sintomatología y nivel
      setSymptomSpecificContent(
        leveledContent[areaId.toLowerCase()][level.toUpperCase()].consequences.map(text => ({
          text,
          // Asignar un tipo basado en el contenido del texto
          type: text.includes('físic') || text.includes('muscular') || text.includes('dormir') || text.includes('sueño') ? 'physical' :
                text.includes('emocion') || text.includes('sentimiento') || text.includes('tristeza') || text.includes('ansiedad') ? 'emotional' :
                text.includes('pensar') || text.includes('concentra') || text.includes('decisiones') || text.includes('memoria') ? 'cognitive' :
                text.includes('social') || text.includes('relacion') || text.includes('aislamiento') ? 'social' :
                text.includes('evita') || text.includes('actividades') || text.includes('comportamiento') ? 'behavioral' : 'mindfulness'
        }))
      );
    } else {
      // Si no hay contenido específico, usar una función de respaldo
      const simulatedPercentage = level === "LEVE" ? 35 : level === "MODERADO" ? 55 : level === "ALTO" ? 85 : 37;
      setSymptomSpecificContent(getConsequencesByLevel(simulatedPercentage, areaId.toLowerCase()));
    }
  }, [areaId, level]);
  
  // Función para obtener consecuencias según el nivel del termómetro y la sintomatología
  const getConsequencesByLevel = (percentage, symptomType) => {
    // Determinar nivel según porcentaje
    let sufferingLevel;
    if (percentage < 20) {
      sufferingLevel = "bajo";
    } else if (percentage < 40) {
      sufferingLevel = "leve";
    } else if (percentage < 60) {
      sufferingLevel = "moderado";
    } else if (percentage < 80) {
      sufferingLevel = "significativo";
    } else {
      sufferingLevel = "intenso";
    }
    
    // Consecuencias según el nivel y el área con categorización por tipo
    const consequencesByArea = {
      ansiedad: {
        bajo: [
          { text: "Ocasionalmente podrías sentir cierta inquietud en situaciones específicas", type: "emotional" },
          { text: "Podría haber momentos de tensión muscular leve después de un día estresante", type: "physical" },
          { text: "Ligera dificultad para desconectar en situaciones puntuales", type: "mindfulness" },
          { text: "Preocupaciones pasajeras sobre temas específicos que no interfieren mucho en tu día a día", type: "cognitive" }
        ],
        leve: [
          { text: "Tensión muscular más frecuente, especialmente en cuello y hombros", type: "physical" },
          { text: "Preocupaciones que pueden persistir durante horas", type: "cognitive" },
          { text: "Algunas dificultades para relajarte completamente", type: "mindfulness" },
          { text: "Cierta inquietud mental ante situaciones cotidianas", type: "emotional" },
          { text: "Podrías comenzar a evitar ligeramente algunas situaciones específicas", type: "behavioral" }
        ],
        moderado: [
          { text: "Preocupación excesiva sobre múltiples temas cotidianos", type: "cognitive" },
          { text: "Síntomas físicos como palpitaciones o dificultades respiratorias ocasionales", type: "physical" },
          { text: "Dificultades para concentrarte o sensación de mente en blanco", type: "mindfulness" },
          { text: "Cansancio notable debido a la tensión constante", type: "physical" },
          { text: "Tendencia a evitar situaciones que te generan malestar", type: "behavioral" },
          { text: "Dificultad para estar presente en conversaciones o actividades por la inquietud", type: "mindfulness" }
        ],
        significativo: [
          { text: "Preocupación casi constante difícil de controlar", type: "cognitive" },
          { text: "Síntomas físicos frecuentes (tensión muscular, palpitaciones, sudoración)", type: "physical" },
          { text: "Dificultad significativa para concentrarte en tareas cotidianas", type: "mindfulness" },
          { text: "Alteraciones del sueño que afectan tu descanso", type: "physical" },
          { text: "Evitación de situaciones sociales o laborales importantes", type: "social" },
          { text: "Sensación frecuente de anticipación catastrófica ante eventos neutrales", type: "cognitive" },
          { text: "Posible irritabilidad o bajo umbral de tolerancia a la frustración", type: "emotional" }
        ],
        intenso: [
          { text: "Ataques de pánico frecuentes o intensos", type: "physical" },
          { text: "Sensación de peligro o catástrofe inminente la mayor parte del tiempo", type: "cognitive" },
          { text: "Síntomas físicos severos que generan temor adicional", type: "physical" },
          { text: "Interferencia severa en todas las áreas de funcionamiento", type: "social" },
          { text: "Evitación significativa que limita tu vida cotidiana", type: "behavioral" },
          { text: "Posibles ideaciones catastrofistas recurrentes", type: "cognitive" },
          { text: "Agotamiento físico y mental por el estado de alerta constante", type: "physical" },
          { text: "Sensación de pérdida de control o miedo a 'volverse loco'", type: "emotional" }
        ]
      },
      tristeza: {
        bajo: [
          { text: "Momentos de melancolía ocasional que pasan con facilidad", type: "emotional" },
          { text: "Pequeños altibajos emocionales dentro de lo normal", type: "emotional" },
          { text: "Cierta reflexión sobre aspectos de tu vida que desearías mejorar", type: "mindfulness" },
          { text: "Ligera disminución temporal de la energía o motivación", type: "physical" }
        ],
        leve: [
          { text: "Sensación de desánimo que aparece más a menudo de lo habitual", type: "emotional" },
          { text: "Ligera disminución de interés en actividades que normalmente disfrutas", type: "behavioral" },
          { text: "Algunos momentos de autodiálogo negativo", type: "cognitive" },
          { text: "Pequeños cambios en tus patrones de sueño o alimentación", type: "physical" },
          { text: "Mayor tendencia al aislamiento en ciertos momentos", type: "social" }
        ],
        moderado: [
          { text: "Sensación frecuente de tristeza o vacío difícil de superar", type: "emotional" },
          { text: "Disminución notable de interés en actividades cotidianas", type: "behavioral" },
          { text: "Sensación de fatiga o falta de energía casi diaria", type: "physical" },
          { text: "Dificultad para concentrarte o tomar decisiones sencillas", type: "cognitive" },
          { text: "Menor capacidad para estar presente en tus experiencias", type: "mindfulness" },
          { text: "Posible disminución del apetito o alimentación emocional", type: "physical" },
          { text: "Tendencia a rumiar sobre el pasado o lamentarte", type: "cognitive" }
        ],
        significativo: [
          { text: "Sentimientos persistentes de tristeza, vacío o desesperanza", type: "emotional" },
          { text: "Pérdida significativa de interés o placer en casi todas las actividades", type: "behavioral" },
          { text: "Fatiga intensa o pérdida de energía casi todos los días", type: "physical" },
          { text: "Sentimientos de inutilidad o culpa excesiva frecuentes", type: "cognitive" },
          { text: "Dificultad para mantenerte presente y conectado con el momento actual", type: "mindfulness" },
          { text: "Aislamiento social progresivo", type: "social" },
          { text: "Alteraciones significativas del sueño (insomnio o hipersomnia)", type: "physical" },
          { text: "Visión pesimista del futuro y sensación de que nada mejorará", type: "cognitive" }
        ],
        intenso: [
          { text: "Estado de ánimo deprimido persistente durante la mayor parte del día", type: "emotional" },
          { text: "Pérdida casi total de interés o placer en todas las actividades", type: "behavioral" },
          { text: "Sentimientos profundos de desesperanza o vacío", type: "emotional" },
          { text: "Desconexión significativa de la propia experiencia presente", type: "mindfulness" },
          { text: "Deterioro significativo del funcionamiento social y laboral", type: "social" },
          { text: "Pensamientos recurrentes sobre la muerte o ideación suicida", type: "cognitive" },
          { text: "Posibles síntomas físicos persistentes sin causa médica clara", type: "physical" },
          { text: "Abandono del autocuidado básico", type: "behavioral" },
          { text: "Pérdida o aumento significativo de peso sin hacer dieta", type: "physical" }
        ]
      },
      estres: {
        bajo: [
          { text: "Ligera sensación de tensión al final del día", type: "physical" },
          { text: "Pequeñas preocupaciones respecto a tareas pendientes", type: "cognitive" },
          { text: "Ocasional dificultad para desconectar del trabajo", type: "mindfulness" },
          { text: "Ligera disminución en la capacidad de disfrutar momentos de ocio", type: "emotional" }
        ],
        leve: [
          { text: "Tensión muscular más frecuente, especialmente al final de la jornada", type: "physical" },
          { text: "Irritabilidad ocasional ante pequeñas contrariedades", type: "emotional" },
          { text: "Ligero cansancio mental después de actividades exigentes", type: "cognitive" },
          { text: "Algunas dificultades para relajarte completamente", type: "mindfulness" },
          { text: "Tendencia a la multitarea con menor eficiencia", type: "behavioral" },
          { text: "Leves alteraciones del sueño, como despertares nocturnos", type: "physical" }
        ],
        moderado: [
          { text: "Sensación de estar sobrepasado/a por responsabilidades y tareas", type: "emotional" },
          { text: "Irritabilidad frecuente o reacciones desproporcionadas", type: "behavioral" },
          { text: "Dificultades para mantener la concentración durante periodos largos", type: "cognitive" },
          { text: "Problemas para conciliar el sueño por pensamientos recurrentes", type: "physical" },
          { text: "Síntomas físicos como dolores de cabeza o molestias digestivas", type: "physical" },
          { text: "Dificultad para estar presente sin anticipar tareas futuras", type: "mindfulness" },
          { text: "Tendencia a posponer actividades de autocuidado", type: "behavioral" },
          { text: "Sensación constante de urgencia y falta de tiempo", type: "emotional" }
        ],
        significativo: [
          { text: "Sensación constante de presión y falta de control", type: "emotional" },
          { text: "Irritabilidad intensa y conflictos frecuentes con el entorno", type: "social" },
          { text: "Agotamiento mental y físico que persiste incluso después de descansar", type: "physical" },
          { text: "Alteraciones significativas del sueño (insomnio, despertar temprano)", type: "physical" },
          { text: "Síntomas físicos persistentes (cefaleas, problemas digestivos, tensión muscular crónica)", type: "physical" },
          { text: "Disminución notable del rendimiento laboral o académico", type: "behavioral" },
          { text: "Casi completa desconexión de la experiencia presente por anticipación constante", type: "mindfulness" },
          { text: "Posible aumento de consumo de sustancias (cafeína, alcohol) para regularse", type: "behavioral" },
          { text: "Abandono de actividades de ocio y tiempo personal", type: "social" }
        ],
        intenso: [
          { text: "Sensación abrumadora de incapacidad para manejar las exigencias diarias", type: "emotional" },
          { text: "Estado de alerta constante y tensión extrema", type: "physical" },
          { text: "Agotamiento extremo que afecta todas las áreas de funcionamiento", type: "physical" },
          { text: "Incapacidad para estar presente incluso en momentos de descanso", type: "mindfulness" },
          { text: "Posible desarrollo de síndrome de burnout con desconexión emocional", type: "emotional" },
          { text: "Síntomas físicos graves y persistentes", type: "physical" },
          { text: "Sentimientos de desesperanza respecto a la posibilidad de mejora", type: "cognitive" },
          { text: "Posible ideación sobre abandono de responsabilidades importantes", type: "behavioral" },
          { text: "Deterioro significativo de relaciones personales por irritabilidad o aislamiento", type: "social" },
          { text: "Sistema inmunológico debilitado con mayor frecuencia de enfermedades", type: "physical" }
        ]
      },
      autoestima: {
        bajo: [
          { text: "Algunos momentos de duda sobre tus habilidades en situaciones nuevas", type: "cognitive" },
          { text: "Ligera tendencia a compararte con otros en ciertas áreas", type: "social" },
          { text: "Ocasional sensación de inseguridad que manejas razonablemente bien", type: "emotional" },
          { text: "Momentos puntuales donde tu atención plena se ve afectada por autocrítica", type: "mindfulness" }
        ],
        leve: [
          { text: "Dudas más frecuentes sobre tus capacidades en áreas específicas", type: "cognitive" },
          { text: "Tendencia a minimizar tus logros o atribuirlos a factores externos", type: "cognitive" },
          { text: "Cierta incomodidad en situaciones sociales nuevas", type: "social" },
          { text: "Anticipación ansiosa ante posibles evaluaciones o críticas", type: "emotional" },
          { text: "Dificultad ocasional para mantenerte presente por autocrítica", type: "mindfulness" },
          { text: "Tendencia a buscar la aprobación de otros en decisiones importantes", type: "behavioral" }
        ],
        moderado: [
          { text: "Diálogo interno crítico que afecta tu confianza regularmente", type: "cognitive" },
          { text: "Dificultad significativa para reconocer tus fortalezas y logros", type: "cognitive" },
          { text: "Comparación frecuente y desfavorable con otras personas", type: "social" },
          { text: "Evitación de situaciones donde podrías ser evaluado/a", type: "behavioral" },
          { text: "Dependencia de la validación externa para sentirte valioso/a", type: "emotional" },
          { text: "Frecuente desconexión de la experiencia presente por rumiación autocrítica", type: "mindfulness" },
          { text: "Posible autocrítica excesiva ante pequeños errores", type: "cognitive" },
          { text: "Dificultad para establecer límites saludables", type: "behavioral" }
        ],
        significativo: [
          { text: "Autocrítica intensa y constante en la mayoría de áreas de tu vida", type: "cognitive" },
          { text: "Creencias limitantes profundamente arraigadas sobre tu valía", type: "cognitive" },
          { text: "Evitación sistemática de situaciones de posible exposición o evaluación", type: "behavioral" },
          { text: "Relaciones personales marcadas por la inseguridad o la dependencia", type: "social" },
          { text: "Distorsiones cognitivas frecuentes que refuerzan la autoimagen negativa", type: "cognitive" },
          { text: "Dificultad para aceptar cumplidos o reconocimiento genuino", type: "emotional" },
          { text: "Incapacidad frecuente para estar presente debido a juicios internos constantes", type: "mindfulness" },
          { text: "Posible autosabotaje en oportunidades personales o profesionales", type: "behavioral" },
          { text: "Perfeccionismo paralizante que dificulta completar tareas", type: "behavioral" }
        ],
        intenso: [
          { text: "Sensación profunda de inadecuación o falta de valía personal", type: "emotional" },
          { text: "Autocrítica destructiva prácticamente constante", type: "cognitive" },
          { text: "Evitación significativa de interacciones sociales por temor al rechazo", type: "social" },
          { text: "Relaciones personales marcadas por la dependencia extrema o el aislamiento", type: "social" },
          { text: "Posibles conductas autodestructivas o de autodesprecio", type: "behavioral" },
          { text: "Impacto severo en el funcionamiento laboral, social y personal", type: "behavioral" },
          { text: "Incapacidad casi total para experimentar el momento presente por juicio constante", type: "mindfulness" },
          { text: "Posible ideación sobre la imposibilidad de ser amado/a o valorado/a", type: "cognitive" },
          { text: "Sistemas de creencias rígidos sobre tus defectos percibidos", type: "cognitive" },
          { text: "Desarrollo de posible ansiedad social o fobia social", type: "social" }
        ]
      },
      insomnio: {
        bajo: [
          { text: "Ocasional dificultad para quedarse dormido en noches puntuales", type: "physical" },
          { text: "Ligera preocupación sobre el sueño en días específicos", type: "cognitive" },
          { text: "Pequeños despertares nocturnos sin mayor impacto", type: "physical" },
          { text: "Posible cansancio leve al día siguiente", type: "physical" }
        ],
        leve: [
          { text: "Mayor frecuencia de noches con dificultad para conciliar el sueño", type: "physical" },
          { text: "Cierta ansiedad anticipatoria a la hora de acostarse", type: "emotional" },
          { text: "Despertares nocturnos más frecuentes", type: "physical" },
          { text: "Disminución ligera del rendimiento diurno", type: "cognitive" },
          { text: "Posible irritabilidad o cambios de humor", type: "emotional" },
          { text: "Dificultad para estar presente durante el día por somnolencia", type: "mindfulness" }
        ],
        moderado: [
          { text: "Dificultad frecuente para quedarse dormido (más de 30 minutos)", type: "physical" },
          { text: "Despertares nocturnos recurrentes con dificultad para volver a dormir", type: "physical" },
          { text: "Ansiedad significativa asociada al momento de dormir", type: "emotional" },
          { text: "Preocupación constante sobre las consecuencias de no dormir bien", type: "cognitive" },
          { text: "Fatiga diurna que afecta notablemente al rendimiento", type: "physical" },
          { text: "Dificultades de concentración y memoria", type: "cognitive" },
          { text: "Reducción del tiempo total de sueño (menos de 6 horas)", type: "physical" },
          { text: "Irritabilidad e inestabilidad emocional", type: "emotional" }
        ],
        significativo: [
          { text: "Insomnio casi diario con gran dificultad para iniciar el sueño", type: "physical" },
          { text: "Múltiples y prolongados despertares nocturnos", type: "physical" },
          { text: "Tiempo total de sueño muy reducido (4-5 horas o menos)", type: "physical" },
          { text: "Fatiga intensa que interfiere significativamente en actividades diarias", type: "physical" },
          { text: "Preocupación obsesiva sobre el sueño", type: "cognitive" },
          { text: "Deterioro notable del rendimiento laboral o académico", type: "behavioral" },
          { text: "Impacto en relaciones interpersonales por irritabilidad", type: "social" },
          { text: "Posible desarrollo de ansiedad o depresión secundarias", type: "emotional" },
          { text: "Búsqueda frecuente de soluciones desesperadas (medicación, alcohol)", type: "behavioral" }
        ],
        intenso: [
          { text: "Insomnio severo y persistente casi todas las noches", type: "physical" },
          { text: "Periodos extremadamente reducidos de sueño o casi ausencia de sueño", type: "physical" },
          { text: "Agotamiento crónico que afecta todas las áreas de funcionamiento", type: "physical" },
          { text: "Miedo intenso a acostarse y enfrentar otra noche de insomnio", type: "emotional" },
          { text: "Deterioro significativo de funciones cognitivas (memoria, concentración, toma de decisiones)", type: "cognitive" },
          { text: "Posible aparición de síntomas físicos asociados a la privación de sueño (debilidad inmunológica, problemas digestivos)", type: "physical" },
          { text: "Riesgo de problemas de salud serios (cardiovasculares, metabólicos)", type: "physical" },
          { text: "Posibles alucinaciones o microsueños en casos extremos", type: "cognitive" },
          { text: "Aislamiento social debido al cansancio extremo", type: "social" },
          { text: "Riesgo de dependencia a medicación para dormir o sustancias", type: "behavioral" }
        ]
      },
      ruptura_pareja: {
        bajo: [
          { text: "Momentos puntuales de tristeza al recordar la relación", type: "emotional" },
          { text: "Algunas dudas sobre decisiones tomadas durante la relación", type: "cognitive" },
          { text: "Cierta incomodidad en situaciones sociales compartidas", type: "social" },
          { text: "Pensamientos ocasionales sobre tu ex pareja", type: "cognitive" }
        ],
        leve: [
          { text: "Sentimientos de tristeza o soledad que aparecen con mayor frecuencia", type: "emotional" },
          { text: "Cierta dificultad para concentrarte cuando surgen recuerdos de la relación", type: "cognitive" },
          { text: "Algunos cambios en tus patrones de sueño o apetito", type: "physical" },
          { text: "Tendencia a revisar redes sociales o buscar información de tu ex pareja", type: "behavioral" },
          { text: "Cierta incomodidad o evitación de lugares asociados a la relación", type: "behavioral" }
        ],
        moderado: [
          { text: "Pensamientos recurrentes sobre la relación y su ruptura", type: "cognitive" },
          { text: "Dificultad para enfocarte en actividades cotidianas por pensamientos intrusivos", type: "mindfulness" },
          { text: "Sentimientos intensos de tristeza, rabia o confusión", type: "emotional" },
          { text: "Tendencia a idealizar la relación pasada o rumiar sobre lo que falló", type: "cognitive" },
          { text: "Evitación activa de situaciones, lugares o personas que recuerden a la relación", type: "behavioral" },
          { text: "Posibles cambios significativos en hábitos de sueño, alimentación o actividad", type: "physical" },
          { text: "Cierta inseguridad sobre tu capacidad para establecer nuevas relaciones", type: "emotional" }
        ],
        significativo: [
          { text: "Dolor emocional intenso que interfiere significativamente en tu vida diaria", type: "emotional" },
          { text: "Rumiación frecuente sobre la relación, con dificultad para concentrarte en otras cosas", type: "cognitive" },
          { text: "Sentimientos persistentes de abandono, rechazo o fracaso personal", type: "emotional" },
          { text: "Alteraciones importantes en patrones de sueño y alimentación", type: "physical" },
          { text: "Retraimiento social significativo o aislamiento", type: "social" },
          { text: "Dificultad para imaginar un futuro sin esa persona", type: "cognitive" },
          { text: "Posibles comportamientos de contacto compulsivo o monitoreo de la ex pareja", type: "behavioral" },
          { text: "Crisis de identidad o sensación de pérdida de dirección en la vida", type: "mindfulness" }
        ],
        intenso: [
          { text: "Dolor emocional abrumador que impide el funcionamiento normal", type: "emotional" },
          { text: "Desesperación o sensación de que no puedes seguir adelante sin esa persona", type: "emotional" },
          { text: "Pensamientos obsesivos sobre la relación o la ex pareja", type: "cognitive" },
          { text: "Posibles conductas impulsivas o autodestructivas para aliviar el malestar", type: "behavioral" },
          { text: "Deterioro significativo en áreas importantes (laboral, académica, familiar)", type: "social" },
          { text: "Síntomas físicos intensos como dolor en el pecho, nauseas o fatiga extrema", type: "physical" },
          { text: "Comportamientos de persecución o acoso a la ex pareja", type: "behavioral" },
          { text: "Ideación sobre reconciliación a cualquier precio o fantasías de rescate", type: "cognitive" },
          { text: "Posibles pensamientos sobre no querer vivir sin esa persona", type: "cognitive" },
          { text: "Alteración severa del sentido de identidad personal", type: "mindfulness" }
        ]
      },
      default: {
        bajo: [
          { text: "Ligera sensación de malestar ocasional", type: "emotional" },
          { text: "Pequeñas dificultades para mantener la concentración en tareas concretas", type: "cognitive" },
          { text: "Momentos puntuales de tensión física", type: "physical" },
          { text: "Ocasional reducción en la capacidad de disfrutar actividades cotidianas", type: "emotional" }
        ],
        leve: [
          { text: "Malestar emocional que aparece con mayor frecuencia", type: "emotional" },
          { text: "Cierta dificultad para concentrarte en tareas que requieren atención sostenida", type: "cognitive" },
          { text: "Tensión física más frecuente y notoria", type: "physical" },
          { text: "Pequeños cambios en patrones de sueño o alimentación", type: "physical" },
          { text: "Cierta tendencia a evitar situaciones que generan incomodidad", type: "behavioral" },
          { text: "Dificultad ocasional para estar plenamente presente", type: "mindfulness" }
        ],
        moderado: [
          { text: "Sensación de estar constantemente al límite de tus capacidades", type: "emotional" },
          { text: "Reducida capacidad para disfrutar momentos de ocio y descanso", type: "behavioral" },
          { text: "Irritabilidad que afecta tus interacciones sociales cercanas", type: "social" },
          { text: "Dificultad creciente para conectar con el momento presente", type: "mindfulness" },
          { text: "Pensamientos recurrentes que interfieren con tu concentración diaria", type: "cognitive" },
          { text: "Síntomas físicos que aparecen con regularidad (tensión, fatiga, dolores)", type: "physical" },
          { text: "Tendencia a utilizar estrategias de evitación que limitan tu vida", type: "behavioral" }
        ],
        significativo: [
          { text: "Emociones negativas intensas que aparecen sin desencadenantes claros", type: "emotional" },
          { text: "Evitación sistemática de situaciones sociales o profesionales importantes", type: "behavioral" },
          { text: "Pensamientos recurrentes sobre fracaso personal o inadecuación", type: "cognitive" },
          { text: "Afectación visible de tu rendimiento laboral o académico", type: "emotional" },
          { text: "Conflictos frecuentes en relaciones significativas por irritabilidad o retraimiento", type: "social" },
          { text: "Sensación persistente de estar desconectado/a de tu propia vida", type: "mindfulness" },
          { text: "Alteraciones significativas en patrones de sueño, apetito o energía", type: "physical" },
          { text: "Posible desarrollo de mecanismos de afrontamiento poco saludables", type: "behavioral" }
        ],
        intenso: [
          { text: "Estado de crisis emocional permanente con incapacidad para experimentar emociones positivas", type: "emotional" },
          { text: "Deterioro grave en todas las áreas vitales: laboral, familiar, social y personal", type: "behavioral" },
          { text: "Agotamiento de recursos internos con necesidad urgente de intervención profesional", type: "physical" },
          { text: "Conductas autodestructivas como única vía de alivio temporal del sufrimiento", type: "behavioral" },
          { text: "Desarrollo o agravamiento de condiciones médicas relacionadas con el estrés crónico", type: "physical" },
          { text: "Pensamientos invasivos sobre la imposibilidad de mejora o salida de la situación", type: "cognitive" },
          { text: "Despersonalización grave: sensación de irrealidad o de no reconocerse a uno mismo", type: "mindfulness" },
          { text: "Pensamiento catastrófico recurrente o ideación suicida", type: "cognitive" },
          { text: "Aislamiento severo con ruptura de vínculos sociales significativos", type: "social" },
          { text: "Síntomas físicos graves y persistentes sin causa médica clara", type: "physical" }
        ]
      }
    };

    // Verificar si existe el área específica, si no, usar default
    const selectedArea = consequencesByArea[symptomType] ? consequencesByArea[symptomType] : consequencesByArea.default;
    
    // Devolver las consecuencias para el nivel correspondiente
    return selectedArea[sufferingLevel];
  };
  
  // Agrupar las consecuencias por tipo para mejorar la visualización
  const groupedConsequences = {
    emotional: symptomSpecificContent.filter(c => c.type === 'emotional'),
    cognitive: symptomSpecificContent.filter(c => c.type === 'cognitive'),
    physical: symptomSpecificContent.filter(c => c.type === 'physical'),
    behavioral: symptomSpecificContent.filter(c => c.type === 'behavioral'),
    social: symptomSpecificContent.filter(c => c.type === 'social'),
    mindfulness: symptomSpecificContent.filter(c => c.type === 'mindfulness')
  };
  
  // Eliminar grupos vacíos
  Object.keys(groupedConsequences).forEach(key => {
    if (groupedConsequences[key].length === 0) {
      delete groupedConsequences[key];
    }
  });
  
  // Nombres legibles para los grupos
  const groupNames = {
    emotional: 'Aspectos Emocionales',
    cognitive: 'Aspectos Cognitivos',
    physical: 'Aspectos Físicos',
    behavioral: 'Aspectos Conductuales',
    social: 'Aspectos Sociales',
    mindfulness: 'Aspectos de Atención Plena'
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };
  
  // Eliminamos la función no utilizada que estaba causando el error ESLint
  
  return (
    <div className="consequences-container">
      <div className="consequences-intro">
        <p className="intro-text">
          <strong>Posibles Consecuencias a Medio Plazo</strong>
        </p>
      </div>
      
      <motion.div 
        className={`consequences-grid ${Object.keys(groupedConsequences).length === 1 ? 'single-consequence' : ''}`}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {Object.keys(groupedConsequences).map((groupKey) => (
          groupedConsequences[groupKey].length > 0 && (
            <motion.div 
              key={groupKey} 
              className="consequence-group"
              variants={itemVariants}
            >
              <div className="group-header" style={{ backgroundColor: `${color}20`, borderColor: color }}>
                <span className="group-icon" style={{ color: color }}>{consequenceIcons[groupKey]}</span>
                <h4>{groupNames[groupKey]}</h4>
              </div>
              
              <ul className="group-items">
                {groupedConsequences[groupKey].map((consequence, idx) => (
                  <motion.li 
                    key={idx}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 * idx }}
                  >
                    {consequence.text}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          )
        ))}
      </motion.div>
      
      <div className="consequences-disclaimer">
        <div className="disclaimer-icon">ℹ️</div>
        <p>Esta información es orientativa, no predictiva. Cada persona vive estas experiencias de forma única.</p>
      </div>
    </div>
  );
};

export default Consequences;
